<template>
  <div>
    <b-modal
      :visible="isVisible"
      size="xl"
      dialog-class="detailed-comparison-modal"
      @hidden="closeModal"
    >
      <template #modal-title>
        <div class="d-flex align-items-center justify-content-between w-100">
          <h5 class="mb-0 font-weight-bold">
            {{ $t('traflMqayis') }}
          </h5>
          <b-button
            variant="outline-secondary" 
            size="sm" 
            class="ml-3"
            @click.prevent="resetOffers"
          >
            <i class="fa fa-refresh mr-1" /> {{ $t('detailedComparisonModal.resetOffers') }}
          </b-button>
        </div>
      </template>

      <div
        class="modal-body position-relative"
        @mousemove="updateTooltipPosition"
      >
        <div
          v-if="tooltip.visible"
          class="hover-tooltip"
          :style="{ left: tooltip.x + 'px', top: tooltip.y + 'px' }"
        >
          {{ tooltip.text }}
        </div>

        <div
          v-for="(offer, offerIndex) in offers"
          :key="offer.id"
          class="offer-container"
        >
          <!-- Company Info Section -->
          <CompanyInfoCard 
            :company="offer.requestUser.company"
            :rejected="offer.rejected"
            @open-company-rate-statistics="openCompanyRateStatistics"
          />
          <!-- Required Conditions Section -->
          <RequiredConditionsSection
            :price-extras="getParsedPricesExtras(offer)"
            :rejected="offer.rejected"
            @get-payment-option-label="getPaymentOptionLabel"
          />

          <!-- Product Comparison Section -->
          <ProductComparisonSection
            v-for="(priceDetail, i) in getParsedPrices(offer)"
            :key="priceDetail.product"
            :price-detail="priceDetail"
            :product="getProduct(priceDetail.product)"
            :offer-id="offer.id"
            :main-rank="rankedOffers[offerIndex].products[i].globalRank"
            :alternative-rank="rankedOffers[offerIndex].products[i].alternativeOffer?.globalRank || 0"
            :rejected="offer.rejected"
            :get-requirement-reply="getRequirementReply"
            :is-photo="isPhoto"
            :get-photo="getPhoto"
            @show-tooltip="showTooltip"
            @hide-tooltip="hideTooltip"
            @select-offer="selectOffer"
          />

          <!-- Offer Totals Section -->
          <OfferTotalsSection
            :main-total="rankedOffers.at(offerIndex).mainTotal"
            :main-rank="rankedOffers.at(offerIndex).mainRank"
            :alternative-total="rankedOffers.at(offerIndex).alternativeTotal"
            :alternative-rank="rankedOffers.at(offerIndex).alternativeRank"
            :selected-total="rankedOffers.at(offerIndex).selectedTotal"
            :rejected="offer.rejected"
          />

          <!-- Offer Validity & Action Buttons -->
          <OfferFooterCard
            :validity-period="getParsedPricesExtras(offer).valid"
            :user-id="offer.requestUser.id"
            :offer-id="offer.id"
            :rejected="rankedOffers.at(offerIndex).rejected"
            :reject-reason="offer.rejectReason"
            :files="getParsedPricesExtras(offer).files ? getParsedPricesExtras(offer).files.split(',').filter(file => file.trim() !== '') : []"
            :is-photo="isPhoto"
            :get-photo="getPhoto"
            @start-conversation="startConversation"
            @open-reject-modal="openRejectModal"
            @undo-reject-offer="undoRejectOffer"
            @open-reason="openReason"
          />
        </div>

        <div class="export-actions-container mt-4 mb-3">
          <div class="d-flex justify-content-center">
            <button
              class="btn btn-success mr-3"
              @click="exportResultsDetailedPrice('xlsx')"
            >
              <i class="fa fa-file-excel-o mr-2" />
              {{ $t('PQTOffersView.excelXar') }}
            </button>
            <button
              class="btn btn-danger"
              @click="exportResultsDetailedPrice('pdf')"
            >
              <i class="fa fa-file-pdf-o mr-2" />
              {{ $t('PQTOffersView.pdfXar') }}
            </button>
          </div>
        </div>
      </div>

      <template #modal-footer>
        <div class="d-flex justify-content-end w-100 modal-footer-compact">
          <button
            class="btn btn-outline-secondary mr-2 btn-sm"
            @click="closeModal"
          >
            <i class="fa fa-times mr-1" />
            {{ $t('tmm') }}
          </button>
          <button
            class="btn btn-primary btn-sm"
            @click="confirm"
          >
            <i class="fa fa-check mr-1" />
            {{ $t('companyProfile.lvEt') }}
          </button>
        </div>
      </template>
    </b-modal>

    <!-- Custom Reject Offer Modal -->
    <RejectOfferModal
      :visible.sync="isRejectModalVisible"
      :offer-id="rejectOfferId"
      @confirm-reject="confirmRejectOffer"
    />

    <!-- Company Rate Statistics Modal -->
    <b-modal
      id="modal-company-rate-statistics"
      centered
      :title="`${openCompanyRatingStatisticPopupProfile} profili`"
      ok-only
      ok-title="Bağla"
      ok-variant="outline-primary"
      header-class="border-bottom-0"
      footer-class="border-top-0"
      content-class="p-4"
      class="company-stats-modal"
    >
      <div class="company-stats-container">
        <CompanyRateStatistics
          v-if="openCompanyRatingStatisticPopup > 0"
          :show-company="true"
          :company-id="openCompanyRatingStatisticPopup"
          :profile-type="openCompanyRatingStatisticPopupProfile"
        />
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  getPaymentOptionLabel,
  rejectOffer,
  resetSelectedOffer,
  selectOffer,
  unRejectOffer
} from "@/repositories/GeneralDataRepository";
import {mapState} from "vuex";
import CompanyRateStatistics from '@/pages/scc/company/CompanyRateStatistics.vue';
import CompanyInfoCard from '@/components/detailed-comparison/CompanyInfoCard.vue';
import RequiredConditionsSection from '@/components/detailed-comparison/RequiredConditionsSection.vue';
import ProductComparisonSection from '@/components/detailed-comparison/ProductComparisonSection.vue';
import OfferTotalsSection from '@/components/detailed-comparison/OfferTotalsSection.vue';
import OfferFooterCard from '@/components/detailed-comparison/OfferFooterCard.vue';
import RejectOfferModal from '@/components/detailed-comparison/RejectOfferModal.vue';

export default {
  name: "DetailedComparisonModal",
  components: {
    CompanyRateStatistics,
    CompanyInfoCard,
    RequiredConditionsSection,
    ProductComparisonSection,
    OfferTotalsSection,
    OfferFooterCard,
    RejectOfferModal
  },
  props: {
    visible: Boolean,
    request_id: Number,
    rankedOffers: Array,
    offers: Array,
    getParsedPrices: Function,
    getProduct: Function,
    getRequirementReply: Function,
    getTechSpecification: Function,
    getParsedPricesExtras: Function,
    startConversation: Function,
    exportResultsDetailedPrice: Function,
    isPhoto: Function,
    getPhoto: Function
  },
  data() {
    return {
      tooltip: {
        visible: false,
        text: '',
        x: 0,
        y: 0
      },
      isVisible: this.visible,
      // For the custom reject modal:
      isRejectModalVisible: false,
      rejectOfferId: null,
      // For company rate statistics modal:
      openCompanyRatingStatisticPopup: 0,
      openCompanyRatingStatisticPopupProfile: 'SUPPLIER'
    };
  },
  computed: {
    ...mapState({
      token: state => state.auth.token,
      user: state => state.auth.user,
      company: state => state.auth.company,
      profile: state => state.auth.profile,
      sessionType: state => state.auth.sessionType,
      position: state => state.auth.position,
    }),
  },
  watch: {
    visible(newValue) {
      this.isVisible = newValue;
    },
    isVisible(newValue) {
      this.$emit("update:visible", newValue);
    },
  },
  methods: {
    // Continuously update tooltip position
    updateTooltipPosition(event) {
      // If .modal-body is position: relative, we can measure relative offsets:
      const rect = event.currentTarget.getBoundingClientRect();
      this.tooltip.x = event.clientX - rect.left;
      this.tooltip.y = event.clientY - rect.top;
    },
    // Show tooltip with given text
    showTooltip(text) {
      this.tooltip.visible = true;
      this.tooltip.text = text;
    },
    // Hide tooltip
    hideTooltip() {
      this.tooltip.visible = false;
    },
    // Method to call when an offer is clicked
    selectOffer(offerId, productId, type) {
      selectOffer(this.token, offerId, productId, type === 'alternative').then(() => {
        this.$emit("refreshData");
      }).catch(error => {
        console.error("Error rejecting offer:", error);
        this.$bvToast.toast("Something went wrong", {variant: "danger"});
      });
    },
    resetOffers() {
      resetSelectedOffer(this.token, this.request_id).then(() => {
        this.$swal({icon: 'success', title: "Uğurla yeniləndi"});
        this.$emit("refreshData");
      }).catch(error => {
        console.error("Error rejecting offer:", error);
        this.$bvToast.toast("Something went wrong", {variant: "danger"});
      });
    },
    openReason(reason) {
      this.$swal({
        title: "Rədd səbəbi",
        text: reason,
        icon: 'warning'

      })
    },
    getPaymentOptionLabel,
    openRejectModal(offerId) {
      this.rejectOfferId = offerId;
      this.rejectReason = "";
      this.rejectValidationMessage = "";
      this.isRejectModalVisible = true;
    },
    confirmRejectOffer(offerId, reason) {
      rejectOffer(this.token, offerId, reason)
          .then(() => {
                this.$bvToast.toast("Rədd edildi", {variant: "success"});
                this.$emit("refreshData");
              }
          )
          .catch(error => {
            console.error("Error rejecting offer:", error);
            this.$bvToast.toast("Something went wrong", {variant: "danger"});
          });
    },
    undoRejectOffer(offerId) {
      // Call your API to undo rejection
      unRejectOffer(this.token, offerId)
          .then(() => {
            this.$bvToast.toast("Rədd etmə əməliyyatı geri götürüldü", {variant: "success"});
            this.$emit("refreshData")
          })
          .catch(error => {
            console.error("Error undoing rejection:", error);
            this.$bvToast.toast("Something went wrong", {variant: "danger"});
          });
    },
    isVat(offer) {
      return offer.isVatPayer === 'true';
    },
    closeModal() {
      this.isVisible = false;
    },
    confirm() {
      this.$emit("confirm");
      this.isVisible = false;
    },
    openCompanyRateStatistics(company) {
      this.$bvModal.show('modal-company-rate-statistics');
      this.openCompanyRatingStatisticPopup = company.id;
      this.openCompanyRatingStatisticPopupProfile = 'SUPPLIER';
    },
    getFileIcon(filename) {
      const extension = filename.split('.').pop().toLowerCase();

      // Map common file extensions to Font Awesome icons
      const iconMap = {
        'pdf': 'fa-file-pdf-o',
        'doc': 'fa-file-word-o',
        'docx': 'fa-file-word-o',
        'xls': 'fa-file-excel-o',
        'xlsx': 'fa-file-excel-o',
        'ppt': 'fa-file-powerpoint-o',
        'pptx': 'fa-file-powerpoint-o',
        'txt': 'fa-file-text-o',
        'zip': 'fa-file-archive-o',
        'rar': 'fa-file-archive-o',
        'jpg': 'fa-file-image-o',
        'jpeg': 'fa-file-image-o',
        'png': 'fa-file-image-o',
        'gif': 'fa-file-image-o'
      };

      return iconMap[extension] || 'fa-file-o';
    }
  }
};
</script>

<style scoped>
/* Modal Styling */
.detailed-comparison-modal .modal-content {
  border-radius: 0.5rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

/* Offer Container */
.offer-container {
  background-color: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  padding: 1.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.offer-container:hover {
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
}

.export-actions-container {
  text-align: center;
}

/* Modal Footer */
.modal-footer-compact {
  padding: 0.5rem;
}

/* Tooltip */
.hover-tooltip {
  position: absolute;
  font-size: 0.75rem;
  transform: translate(-50%, -50%);
  background-color: rgba(0, 123, 255, 0.9);
  color: #fff;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;
  font-weight: 600;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 9999;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(0, 123, 255, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(0, 123, 255, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(0, 123, 255, 0);
  }
}
</style>
